import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import Slider from 'react-slick';
import { motion } from 'framer-motion';

// Simple Loading Spinner to avoid dependency issues
const SimpleSpinner = () => (
    <div className="flex justify-center items-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
);

const ProductDetailsPage = ({ useAuth, useCartRefresh, API }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, token } = useAuth();
    const { refreshCart } = useCartRefresh();

    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedSize, setSelectedSize] = useState('');
    const [selectedColor, setSelectedColor] = useState('');
    const [addingToCart, setAddingToCart] = useState(false);
    const [mainImage, setMainImage] = useState('');

    useEffect(() => {
        fetchProductDetails();
        window.scrollTo(0, 0);
    }, [id]);

    const fetchProductDetails = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API}/products/${id}`);
            setProduct(response.data);
            setMainImage(response.data.image_url);
            // Set default selections if available
            if (response.data.colour && response.data.colour.length > 0) {
                setSelectedColor(response.data.colour[0]);
            }
            if (response.data.size && response.data.size.length > 0) {
                setSelectedSize(response.data.size[0]);
            }
        } catch (err) {
            console.error('Error fetching product:', err);
            setError('Failed to load product details');
        } finally {
            setLoading(false);
        }
    };

        const addToCart = async (silent = false) => {
        if (!user) {
            alert('Please login to add items to cart');
            navigate('/login');
            return;
        }

        setAddingToCart(true);
        try {
            const config = { headers: { Authorization: `Bearer ${token}` } };
            await axios.post(`${API}/cart/add`, {
                product_id: product._id,
                quantity: 1,
                size: selectedSize,
                color: selectedColor
            }, config);

            if (refreshCart) refreshCart();
            // Only show alert if NOT silent
            if (!silent) alert('Product added to cart!');
        } catch (error) {
            console.error('Error adding to cart:', error);
            alert(error.response?.data?.message || 'Error adding product to cart');
        } finally {
            setAddingToCart(false);
        }
    };

    const handleBuyNow = async () => {
        if (!user) {
            alert('Please login to buy items');
            navigate('/login');
            return;
        }
        
        // Pass true to suppress the alert
        await addToCart(true); 
        navigate('/checkout');
    };
    if (loading) return <SimpleSpinner />;
    if (error) return <div className="text-center text-red-600 p-12">{error}</div>;
    if (!product) return <div className="text-center p-12">Product not found</div>;

    return (
        <div className="min-h-screen bg-gray-50 pt-24 pb-12">
            <div className="container mx-auto px-4">
                <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-8">
                        {/* Image Section */}
                        <div className="space-y-4">
                            <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden rounded-2xl border border-gray-100">
                                <img
                                    src={mainImage}
                                    alt={product.name}
                                    className="w-full h-full object-cover object-center transform hover:scale-105 transition-transform duration-500"
                                />
                            </div>
                            {/* Thumbnail Gallery (if multiple images existed, currently just one main image usually) */}
                        </div>

                        {/* Details Section */}
                        <div className="flex flex-col space-y-6">
                            <div>
                                <span className="text-blue-600 font-semibold tracking-wide uppercase text-sm bg-blue-50 px-3 py-1 rounded-full">
                                    {product.category}
                                </span>
                                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-2">
                                    {product.name}
                                </h1>
                                <div className="flex items-center space-x-4">
                                    <span className="text-3xl font-bold text-gray-900">₹{product.price}</span>
                                    {product.original_price && (
                                        <span className="text-xl text-gray-400 line-through">₹{product.original_price}</span>
                                    )}
                                    {product.stock > 0 ? (
                                        <span className="text-green-600 font-medium px-2 py-1 bg-green-50 rounded-lg text-sm">
                                            In Stock
                                        </span>
                                    ) : (
                                        <span className="text-red-600 font-medium px-2 py-1 bg-red-50 rounded-lg text-sm">
                                            Out of Stock
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="prose prose-blue text-gray-600">
                                <p>{product.description}</p>
                            </div>

                            {/* Options */}
                            <div className="space-y-6 border-t border-b border-gray-100 py-6">
                                {/* Colors */}
                                {product.colour && product.colour.length > 0 && (
                                    <div>
                                        <h3 className="text-sm font-medium text-gray-900 mb-3">Color</h3>
                                        <div className="flex flex-wrap gap-3">
                                            {product.colour.map((color) => (
                                                <button
                                                    key={color}
                                                    onClick={() => setSelectedColor(color)}
                                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${selectedColor === color
                                                            ? 'bg-gray-900 text-white shadow-lg ring-2 ring-offset-2 ring-gray-900'
                                                            : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                                                        }`}
                                                >
                                                    {color}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Sizes (Assuming size array exists in product model, if not we can hide this) */}
                                {product.size && product.size.length > 0 && (
                                    <div>
                                        <h3 className="text-sm font-medium text-gray-900 mb-3">Size</h3>
                                        <div className="flex flex-wrap gap-3">
                                            {product.size.map((size) => (
                                                <button
                                                    key={size}
                                                    onClick={() => setSelectedSize(size)}
                                                    className={`w-12 h-12 rounded-lg text-sm font-medium flex items-center justify-center transition-all ${selectedSize === size
                                                            ? 'bg-blue-600 text-white shadow-lg ring-2 ring-offset-2 ring-blue-600'
                                                            : 'bg-white border border-gray-200 text-gray-900 hover:border-blue-400'
                                                        }`}
                                                >
                                                    {size}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                             {/* Actions */}
                            <div className="flex gap-4 pt-4">
                                <button
                                    onClick={addToCart}
                                    disabled={addingToCart || product.stock === 0}
                                    className={`flex-1 py-4 px-6 rounded-xl font-bold text-lg border-2 transition-all flex items-center justify-center gap-2 ${
                                        product.stock === 0
                                            ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                                            : 'border-purple-600 text-purple-600 hover:bg-purple-50'
                                    }`}
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                    Add to Cart
                                </button>
                                
                                <button
                                    onClick={handleBuyNow}
                                    disabled={addingToCart || product.stock === 0}
                                    className={`flex-1 py-4 px-6 rounded-xl font-bold text-lg shadow-xl transition-all transform hover:-translate-y-1 flex items-center justify-center gap-2 ${
                                        product.stock === 0
                                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                            : 'bg-purple-700 text-white hover:bg-purple-800 hover:shadow-2xl'
                                    }`}
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                                    </svg>
                                    Buy Now
                                </button>
                            </div>

                            {/* Additional Info */}
                            <div className="grid grid-cols-2 gap-4 text-sm text-gray-500 pt-6">
                                <div className="flex items-center gap-2">
                                    <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span>Authentic Quality</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span>Secure Payment</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProductDetailsPage;
